import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import {
	EntityType,
	lowercaseFirstLetter,
	InputMapState,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
	READABLE_ENTITY_TYPES,
	PassThroughCallableEntityState,
	TerminationEntityState,
	ChangeSet,
	ProjectState,
	getParentCanvasEntity,
	TERMINATION_TYPES,
	VARIABLE_TYPES,
	StateMutationAction,
  safeResolveEntityName,
  entityDisabledReasonToShortReadableError
} from '@elyx-code/project-logic-tree';
import './draggable-entity-card.css';
import {
	getCanvasEntityDOMId,
	getCardColorCSSVariable,
	getCardSupportColorCSSVariable,
	highlightElementIndefinetly,
	highlightElementSuccess,
	removeHighlightElementIndefinetly,
	removeHighlightElementSuccess,
	resolveEntityColorClass
} from '../../services/editor/ui';
import Button from '../../lib/button/Button';
import { Canvas } from '../../lib/canvas';
import DataTypeIcon from './DataTypeIcon';
import { ConnectionNodeType, EditorService } from '../../services/editor';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import LogicErrorIconMenu from './logic-error/LogicErrorIconMenu';
import { CanvasEntityElement } from '../../services/editor/CanvasElement';
import DynamicValueEntry from '../dynamic-value/DynamicValueEntry';
import { resolveEntityDataTypeIcon } from '../../services/editor/icons';
import EntityLockedIconMenu from './entity-locked-icon/EntityLockedIcon';
import { mergeRefs } from '../../utils/react';
import { popupNotification } from '../../popup-notification';

interface IInputMapPropertyProps {
	inputMap: InputMapState;
	entity: PassThroughCallableEntityState | TerminationEntityState;
	canvasElement: CanvasEntityElement;
	size?: 'small' | 'default';
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
}

export default function InputMapProperty(props: IInputMapPropertyProps) {
	if (props.project.destroyed) {
		return null;
	}

	const rootElementRef = useRef(null);

	const handleEdit = useCallback(async () => {
		props.project?.canvas?.unselectAll();

		const isInferredProperty =
			props.inputMap.declaration?.type === EntityType.Property &&
			props.inputMap.declaration?.parent.type ===
				EntityType.DefinitionEntity &&
			props.inputMap.declaration?.parent.parent.type ===
				EntityType.DataType;

		const isSearchInput =
			props.inputMap.declaration?.type === EntityType.ValueDescriptor &&
			props.inputMap.declaration?.parent.type === EntityType.Search;

		if (isInferredProperty || isSearchInput) {
			EditorService.navigate(
				`${props.project?.editorBasePath}/entity/${props.inputMap.declaration?.type}/${props.inputMap.declaration?.id}`
			);

			return;
		}

		EditorService.navigate(
			`${props.project?.editorBasePath}/entity/${EntityType.InputMap}/${props.inputMap.id}`
		);
	}, [props.project, props.inputMap]);

	useEffect(() => {
		const el = rootElementRef.current as any as HTMLElement;
		if (!el) return;

		const handleDoubleClick = (e: MouseEvent) => {
			if ((e as any).__canvasIgnore) return;
			(e as any).__canvasIgnore = true;
			handleEdit();
		};

		const handleMouseDown = (e: MouseEvent) => {
			if ((e as any).__canvasIgnore) return;
			(e as any).__canvasIgnore = true;
		};

		el.addEventListener('dblclick', handleDoubleClick, false);
		el.addEventListener('mousedown', handleMouseDown, false);

		return () => {
			el.removeEventListener('dblclick', handleDoubleClick, false);
			el.removeEventListener('mousedown', handleMouseDown, false);
		};
	}, [handleEdit]);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	const inputMap = props.inputMap;

	const name = safeResolveEntityName(inputMap, props.project.logic);

	const isConnected = !!inputMap?.readsValue;

	const relevantParentDisabledReasons = props.parentDisabledReasons.filter(
		(reason) =>
			reason !==
			CanvasEntityConnectionDisabledReason.IsNotValueReadingEntity
	);

	// Input maps are always user managed, but how much you can do with them depends on what their declaration is
	//
	const representsInlinedUserManagedDeclaration =
		!props.inputMap.declaration ||
		// If it represents an inferred property
		(props.inputMap.declaration?.type === EntityType.Property &&
			props.inputMap.declaration?.parent?.type ===
				EntityType.DefinitionEntity &&
			props.inputMap.declaration?.parent?.parent.type ===
				EntityType.DataType) ||
		// If it represents a search statement input
		(props.inputMap.declaration?.type === EntityType.ValueDescriptor &&
			props.inputMap.declaration?.parent?.type === EntityType.Search);

	// When the entity is part of a variable declaration,
	// which has one of their internal calls being executed,
	// we can't connect values to it
	const parentVarUsesInternalCalls =
		(props.entity.type === EntityType.VariableDeclaration ||
			props.entity.type === EntityType.VariableInstance) &&
		props.entity.internalCalls.some((call) => !!call.calledBy.length);

	// If the parent is a suggestion or itself is a suggestion, add the suggestion class
	const disabled =
		!!(
			(!!relevantParentDisabledReasons.length ||
				inputMap.suggestion ||
				// @ts-ignore
				props.entity.suggestion) &&
			!props.project.onGoingConnectionCompatibleEntities?.[
				props.inputMap.id
			]
		) ||
		parentVarUsesInternalCalls ||
		(props.entity.type === EntityType.VariableInstance &&
			inputMap.declaration.type === EntityType.Property &&
			inputMap.declaration.constant);

	const canDelete =
		representsInlinedUserManagedDeclaration ||
		(!!props.inputMap.declaration?.id &&
			!props.project.logic.getBuiltIn(props.inputMap.declaration?.id) &&
			!props.inputMap.declaration?.required &&
			props.inputMap.deletable);

	const dynamicValuePassDownDisabledReasons = [
		...relevantParentDisabledReasons,
		...(parentVarUsesInternalCalls
			? [
					CanvasEntityConnectionDisabledReason.CannotInteractWithInputInVariableWithInternalCalls
				]
			: []),
		...(props.entity.type === EntityType.VariableInstance &&
		inputMap.declaration.type === EntityType.Property &&
		inputMap.declaration.constant
			? [
					CanvasEntityConnectionDisabledReason.PropertyIsConstantItsValueCantBeReset
				]
			: [])
	];

	const memoizedCanvasPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={
					'canvas-property-entity-card-menu-button-' +
					props.inputMap.id +
					'--popup--body'
				}
			>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								const isInferredProperty =
									props.inputMap.declaration?.type ===
										EntityType.Property &&
									props.inputMap.declaration?.parent.type ===
										EntityType.DefinitionEntity &&
									props.inputMap.declaration?.parent.parent
										.type === EntityType.DataType;

								const isSearchInput =
									props.inputMap.declaration?.type ===
										EntityType.ValueDescriptor &&
									props.inputMap.declaration?.parent.type ===
										EntityType.Search;

								if (isInferredProperty || isSearchInput) {
									await props.project.remove(
										props.inputMap.declaration?.id
									);
								}

								await props.project.remove(props.inputMap.id);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !canDelete,
							tooltip: (() => {
								if (representsInlinedUserManagedDeclaration) {
									const isInferredProperty =
										props.inputMap.declaration?.type ===
											EntityType.Property &&
										props.inputMap.declaration?.parent
											.type ===
											EntityType.DefinitionEntity &&
										props.inputMap.declaration?.parent
											.parent.type ===
											EntityType.DataType;

									const isSearchInput =
										props.inputMap.declaration?.type ===
											EntityType.ValueDescriptor &&
										props.inputMap.declaration?.parent
											.type === EntityType.Search;

									let suffix = 'input';

									if (isInferredProperty) {
										suffix = `${lowercaseFirstLetter(
											READABLE_ENTITY_TYPES[
												EntityType.Property
											].singular
										)} from all variable instances`;
									}

									if (isSearchInput) {
										suffix = 'search input';
									}

									if (!inputMap.declaration?.editable) {
										return `This ${suffix} cannot be deleted. Because the system need to refer to it over time.`;
									}

									return `Forever delete this ${suffix}`;
								}

								if (
									!representsInlinedUserManagedDeclaration &&
									props.inputMap.declaration?.required
								) {
									return 'Cannot delete this input because it is required';
								}

								if (!inputMap.deletable) {
									return 'This input cannot be deleted. Because the system need to refer to it over time.';
								}

								return 'Cannot delete this input because it is not user managed';
							})(),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'--' +
								props.inputMap.type +
								'--' +
								props.inputMap.id +
								'--delete'
						},
						{
							project: props.project,
							onClick: async () => {
								props.project?.canvas?.unselectAll();

								const isInferredProperty =
									props.inputMap.declaration?.type ===
										EntityType.Property &&
									props.inputMap.declaration?.parent.type ===
										EntityType.DefinitionEntity &&
									props.inputMap.declaration?.parent.parent
										.type === EntityType.DataType;

								const isSearchInput =
									props.inputMap.declaration?.type ===
										EntityType.ValueDescriptor &&
									props.inputMap.declaration?.parent.type ===
										EntityType.Search;

								if (isInferredProperty || isSearchInput) {
									EditorService.navigate(
										`${props.project?.editorBasePath}/entity/${props.inputMap.declaration?.type}/${props.inputMap.declaration?.id}`
									);

									return;
								}

								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${EntityType.InputMap}/${props.inputMap.id}`
								);
							},
							label: 'Edit',
							iconEnd: 'fa-solid fa-pencil',
							disabled: false,
							tooltip: (() => {
								if (representsInlinedUserManagedDeclaration) {
									const isInferredProperty =
										props.inputMap.declaration?.type ===
											EntityType.Property &&
										props.inputMap.declaration?.parent
											.type ===
											EntityType.DefinitionEntity &&
										props.inputMap.declaration?.parent
											.parent.type ===
											EntityType.DataType;

									const isSearchInput =
										props.inputMap.declaration?.type ===
											EntityType.ValueDescriptor &&
										props.inputMap.declaration?.parent
											.type === EntityType.Search;

									let suffix = 'input';

									if (isInferredProperty) {
										suffix = `${lowercaseFirstLetter(
											READABLE_ENTITY_TYPES[
												EntityType.Property
											].singular
										)} for all variable instances`;
									}

									if (isSearchInput) {
										suffix = 'search input';
									}

									if (!inputMap.declaration?.editable) {
										return `This ${suffix} has been locked and can no longer be edited`;
									}

									return `Edit this ${suffix}`;
								}

								if (!inputMap.editable) {
									return `This input has been locked and can no longer be edited`;
								}

								return 'Edit this input';
							})(),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'--' +
								props.inputMap.type +
								'--' +
								props.inputMap.id +
								'--edit'
						},
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											props.inputMap,
											false,
											StateMutationAction.RecalculateInputMap
										)
									);

								props.inputMap.afterAllChildrenInitialized(
									changeSet
								);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.inputMap.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.inputMap.type +
								'-card--' +
								props.inputMap.id +
								'--recalculate'
						},
						...(!canDelete
							? [
									{
										project: props.project,
										onClick: async () => {
											await props.project.remove(
												props.inputMap.id
											);
										},
										label: 'Force delete',
										color: 'error',
										iconEnd: 'fa-solid fa-trash-can',
										tooltip: 'Forever delete this input',
										parentCanvasObject: popup.self,
										id:
											'canvas--' +
											props.inputMap.type +
											'--' +
											props.inputMap.id +
											'--force-delete'
									}
								]
							: ([] as any)),
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(
									props.inputMap.id
								);
								popupNotification({
									text: `Copied ID: ${props.inputMap.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.inputMap.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.inputMap
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.inputMap.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		),
		[canDelete]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-property-entity-card-menu-button-' +
				props.inputMap.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-property-card-menu-button-' + props.inputMap.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-property-entity-card-menu-button-' +
				props.inputMap.id +
				'--popup--body'
		},
		memoizedCanvasPopupBody
	).onBeforeShow(() => !disabled);

	const memoizedCanvasDisabledTooltipBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				id={
					'canvas-input-map-disabled-tooltip-' +
					props.inputMap.id +
					'--popup--body'
				}
				containerClasses={[
					'tooltip',
					'tooltip--large-font',
					'tooltip--dynamic-value'
				]}
			>
				<h3 style={{ marginTop: 0, marginBottom: 5 }}>
					{dynamicValuePassDownDisabledReasons.map(
						(reason, index) => (
							<span key={index}>
								{entityDisabledReasonToShortReadableError(
									reason
								)}
								{index <
								dynamicValuePassDownDisabledReasons.length -
									1 ? (
									<br />
								) : null}
							</span>
						)
					)}
				</h3>
			</CanvasPopupBaseComponent>
		),
		[dynamicValuePassDownDisabledReasons]
	);

	const canvasDisabledTooltip = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-input-map-disabled-tooltip-' +
				props.inputMap.id +
				'--popup',
			anchorQuerySelector: '#' + getCanvasEntityDOMId(inputMap),
			placement: 'top',
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-input-map-disabled-tooltip-' +
				props.inputMap.id +
				'--popup--body'
		},
		memoizedCanvasDisabledTooltipBody,
		[dynamicValuePassDownDisabledReasons]
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600
		})
		.onBeforeShow(
			() =>
				!!dynamicValuePassDownDisabledReasons.length &&
				// We don't show the tooltip when the user is drawing a connection because that action has its own feedback
				!props.project.onGoingConnectionNodeType
		);

	const isInputOfGlobalVariableDeclaration =
		props.entity.type === EntityType.VariableDeclaration &&
		props.entity.parent.type === EntityType.Project;
	const nodeDraggingDisabled =
		isInputOfGlobalVariableDeclaration || !!props.inputMap.readsValue;

	const resolveTooltip = () => {
		if (isInputOfGlobalVariableDeclaration) {
			return (
				<h3 style={{ marginTop: 0, marginBottom: 5 }}>
					Cannot interact with inputs in global variable declarations
				</h3>
			);
		} else if (!!props.inputMap.readsValue) {
			return (
				<h3 style={{ marginTop: 0, marginBottom: 5 }}>
					Cannot connect multiple value writers. This input is already
					connected to a value.
				</h3>
			);
		}
		return (
			<h3 style={{ marginTop: 0, marginBottom: 5 }}>
				Click and drag to connect to another value
			</h3>
		);
	};

	const memoizedCanvasSocketTooltipBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				id={
					'canvas-input-map-socket-tooltip-' +
					props.inputMap.id +
					'--popup--body'
				}
				containerClasses={['tooltip', 'tooltip--large-font']}
			>
				{resolveTooltip()}
			</CanvasPopupBaseComponent>
		),
		[]
	);

	const canvasSocketTooltip = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-input-map-socket-tooltip-' +
				props.inputMap.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-input-map-socket-tooltip-' +
				props.inputMap.id +
				'--popup',
			placement: 'left',
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-input-map-socket-tooltip-' +
				props.inputMap.id +
				'--popup--body'
		},
		memoizedCanvasSocketTooltipBody,
		[]
	).setupHover({
		openDelay: nodeDraggingDisabled ? 0 : 600,
		closeDelay: 400
	});

	const parentNode = getParentCanvasEntity(props.inputMap);
	// const valueOwnerParentNode = parentNode?.type === EntityType.VariableInstance ? parentNode.declaration : parentNode;
	const parentValueOwnerListenerTarget = [
		...VARIABLE_TYPES,
		...TERMINATION_TYPES
	].includes(parentNode?.type as EntityType)
		? parentNode
		: null;

	useEffect(() => {
		const handleInputMapUpdate = () => {
			forceUpdate();
		};

		const handleConnectionStart = (payload: any) => {
			if (
				!!payload.compatible?.[props.inputMap.id] &&
				[ConnectionNodeType.PropertyOut].includes(payload.nodeType)
			) {
				if (rootElementRef.current) {
					highlightElementIndefinetly(rootElementRef.current);
				}
			}
		};

		const handleConnectionEnd = () => {
			if (rootElementRef.current) {
				removeHighlightElementIndefinetly(rootElementRef.current);
				removeHighlightElementSuccess(rootElementRef.current);
			}
		};

		const handleValueChange = (payload: any) => {
			if (
				payload?.owner?.id === props.inputMap?.id ||
				payload?.owner?.id === props.inputMap?.declaration?.id ||
				payload?.owner?.id === parentValueOwnerListenerTarget?.id
			) {
				forceUpdate();
			}
		};

		props.project.on(props.inputMap.id, handleInputMapUpdate);
		props.project.on('canvas-connection-start', handleConnectionStart);
		props.project.on('canvas-connection-end', handleConnectionEnd);
		props.project.on('value-written', handleValueChange);
		props.project.on('value-removed', handleValueChange);

		return () => {
			props.project.off(props.inputMap.id, handleInputMapUpdate);
			props.project.off('canvas-connection-start', handleConnectionStart);
			props.project.off('canvas-connection-end', handleConnectionEnd);
			props.project.off('value-written', handleValueChange);
			props.project.off('value-removed', handleValueChange);
		};
	}, [
		props.project,
		props.inputMap,
		updateKey,
		rootElementRef,
		highlightElementIndefinetly,
		removeHighlightElementIndefinetly,
		removeHighlightElementSuccess
	]);

	const size = props.size || 'default';

	const iconConfig = useMemo(
		() => resolveEntityDataTypeIcon(inputMap),
		[props.project, inputMap, updateKey]
	);

	const mergedRefs = !!canvasDisabledTooltip
		? mergeRefs(rootElementRef, canvasDisabledTooltip.anchorRef)
		: rootElementRef;

	return (
		<li
			ref={mergedRefs}
			className={[
				'canvas-draggable-card__property-row',
				'canvas-draggable-card__standard-property',
				size === 'small' ? 'canvas-draggable-card__property__small' : ''
			].join(' ')}
			id={getCanvasEntityDOMId(inputMap)}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(inputMap)
			}}
			onMouseOver={(e: any) => {
				if (!!props.project.canvas?.activeConnectionDrawing) {
					if (
						props.project.onGoingConnectionCompatibleEntities?.[
							props.inputMap.id
						]
					) {
						if (
							props.project.onGoingConnectionSource &&
							[ConnectionNodeType.PropertyOut].includes(
								props.project.onGoingConnectionNodeType
							)
						) {
							props.project.onGoingConnectionTarget =
								props.inputMap;
							props.project.onGoingConnectionTargetId =
								getCanvasEntityDOMId(inputMap);
						}

						props.project.canvas?.activeConnectionDrawing.snap(
							getCanvasEntityDOMId(inputMap)
						);

						removeHighlightElementIndefinetly(
							rootElementRef.current
						);
						highlightElementSuccess(rootElementRef.current);
					} else if (
						!!props.project.onGoingConnectionIncompatibleEntities?.[
							props.inputMap.id
						]
					) {
						// Read the disabled reasons and add a tooltip to the mouse
						const reasonsMessage =
							props.project.onGoingConnectionIncompatibleEntities?.[
								props.inputMap.id
							].reasons
								.map((reason) =>
									entityDisabledReasonToShortReadableError(
										reason
									)
								)
								.join('.\n');

						props.project.canvas?.showTooltip(
							'connection-disabled-reasons-input-map-' +
								props.inputMap.id,
							reasonsMessage
						);
						if (props.project.canvas) {
							props.project.canvas.tooltip.style.fontSize =
								'28px';
							props.project.canvas.canvasElement.style.cursor =
								'not-allowed';
						}
					}
				}
			}}
			onMouseLeave={(e: any) => {
				if (
					props.project.canvas?.activeUserSetTooltipId ===
					'connection-disabled-reasons-input-map-' + props.inputMap.id
				) {
					props.project.canvas?.hideTooltip(
						'connection-disabled-reasons-input-map-' +
							props.inputMap.id
					);
				}

				if (
					!!props.project.canvas?.activeConnectionDrawing &&
					props.project.onGoingConnectionCompatibleEntities?.[
						props.inputMap.id
					]
				) {
					removeHighlightElementIndefinetly(rootElementRef.current);
					highlightElementIndefinetly(rootElementRef.current);

					if (
						props.project.canvas?.activeConnectionDrawing
							?.snappedTo === getCanvasEntityDOMId(inputMap)
					) {
						props.project.canvas?.activeConnectionDrawing?.unSnap();
					}

					if (
						props.project.onGoingConnectionTarget ===
							props.inputMap &&
						props.project.onGoingConnectionTargetId ===
							getCanvasEntityDOMId(inputMap) &&
						[ConnectionNodeType.PropertyOut].includes(
							props.project.onGoingConnectionNodeType
						)
					) {
						props.project.onGoingConnectionTarget = null;
						props.project.onGoingConnectionTargetId = null;
					}
				}
			}}
		>
			<div
				id={`${getCanvasEntityDOMId(inputMap)}--connected-left-node`}
				{...{
					[Canvas.OBJ_ATTR_NAME]: `${getCanvasEntityDOMId(
						inputMap
					)}--connected-left-node`
				}}
				className={[
					'canvas__small-node',
					'canvas__small-node--position',
					'canvas__node__left',
					...(isConnected
						? ['canvas-property__node__connected']
						: [
								'canvas__medium-node',
								resolveEntityColorClass(props.entity.type)
							])
				].join(' ')}
				style={{
					position: 'absolute',
					top: '50%',
					left: '-9px',
					transform: 'translateY(-50%)',
					width: '10px',
					height: '18px',
					padding: 0,
					background: 'transparent',
					border: 'none',
					...(props.project.canvas?.disabled
						? { cursor: 'not-allowed', opacity: 0.9 }
						: { cursor: 'pointer' }),
					zIndex: 1
				}}
			>
				<svg
					width="10"
					height="18"
					viewBox="0 0 10 18"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					style={{ display: 'block' }}
				>
					<path
						d="M 12 -2 L 3 7 Q 1.5 9 3 11 L 12 20 Z"
						fill={getCardSupportColorCSSVariable(props.entity)}
						stroke={getCardColorCSSVariable(props.entity)}
						strokeWidth="2"
					/>
				</svg>
			</div>
			{!disabled && (
				<button
					id={`${getCanvasEntityDOMId(inputMap)}--new-left-node`}
					className={[
						'canvas__small-node',
						'canvas__small-node--position',
						'canvas-property__node',
						'canvas__node__left',
						'canvas__node__new',
						'canvas__medium-node',
						...(nodeDraggingDisabled ? ['disabled'] : [])
					].join(' ')}
					ref={canvasSocketTooltip.anchorRef}
					style={{
						top: '50%',
						transform: 'translateY(-50%)',
						...(nodeDraggingDisabled
							? {
									cursor: 'not-allowed',
									backgroundColor: 'var(--color-light-grey)'
								}
							: {})
					}}
					onMouseDown={(e) => {
						if (nodeDraggingDisabled) {
							return;
						}

						e.stopPropagation();

						const permanentNodeElement =
							rootElementRef.current.querySelector(
								`#${getCanvasEntityDOMId(inputMap)}--connected-left-node`
							);

						// Add 'canvas__node__active' class to the element
						permanentNodeElement?.classList.add(
							'canvas__node__active'
						);

						// Listen to the mouseup event to remove the class
						const mouseUpListener = (e: MouseEvent) => {
							if (permanentNodeElement) {
								const isConnectedToMenu =
									props.project.onGoingConnectionTarget ===
									inputMap;

								if (!isConnectedToMenu) {
									permanentNodeElement.classList.remove(
										'canvas__node__active'
									);
								}
							}

							if (
								props.project.onGoingConnectionTarget !==
								inputMap
							) {
								props.project.off(
									'canvas-connection-end',
									mouseUpListener
								);
							}
						};

						props.project.on(
							'canvas-connection-end',
							mouseUpListener
						);

						props.project.startConnection({
							nodeType: ConnectionNodeType.PropertyIn,
							target: inputMap,
							targetId: `${getCanvasEntityDOMId(
								inputMap
							)}--connected-left-node`,
							draggableObject: props.canvasElement.draggableObject
						});
					}}
					onMouseUp={(e) => {
						// Remove active class from connected/permanent node
						const permanentNodeElement =
							rootElementRef.current.querySelector(
								`#${getCanvasEntityDOMId(inputMap)}--connected-left-node`
							);

						permanentNodeElement?.classList.remove(
							'canvas__node__active'
						);
					}}
				/>
			)}
			<DynamicValueEntry
				entity={props.inputMap}
				project={props.project}
				canvasElement={props.canvasElement}
				parentDisabledReasons={dynamicValuePassDownDisabledReasons}
			>
				{(extraContent) => (
					<>
						<div
							className={[
								'canvas-draggable-card__property__left-block',
								'canvas-draggable-card__property__value-container'
							].join(' ')}
						>
							<DataTypeIcon
								entity={inputMap}
								size={size === 'small' ? 'small' : 'medium'}
								color="dark-grey"
								parentDisabledReasons={
									dynamicValuePassDownDisabledReasons
								}
								style={{
									position: 'relative',
									left: !!iconConfig.url ? '-4px' : '0'
								}}
								project={props.project}
								parentCanvasObject={
									props.canvasElement?.draggableObject
								}
							/>
							<span
								className={[
									'canvas-draggable-card__property__labels-group'
								].join(' ')}
								style={{
									position: 'relative',
									left: !!iconConfig.url ? '-12px' : '0'
								}}
							>
								<p
									className={[
										'canvas-draggable-card__property__name',
										...(disabled ? ['suggestion'] : [])
									].join(' ')}
								>
									{name}
								</p>
								{extraContent}
							</span>
						</div>
						<span
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '6px'
							}}
						>
							{!disabled &&
								!dynamicValuePassDownDisabledReasons.length && (
									<div className="canvas-draggable-card__property-row__menu-button">
										<Button
											variant="text"
											color="grey"
											iconEnd="fa-solid fa-ellipsis-v"
											size="small"
											className="canvas-small-entity-card-menu-button"
											ref={canvasPopup.anchorRef}
											id={`canvas-property-card-menu-button-${inputMap.id}`}
											onClick={(e: any) => {
												e.stopPropagation();

												canvasPopup?.toggle();
											}}
											onDoubleClick={(
												e: React.MouseEvent<HTMLButtonElement>
											) => {
												e.stopPropagation();
												const isInferredProperty =
													props.inputMap.declaration
														?.type ===
														EntityType.Property &&
													props.inputMap.declaration
														?.parent.type ===
														EntityType.DefinitionEntity &&
													props.inputMap.declaration
														?.parent.parent.type ===
														EntityType.DataType;

												const isSearchInput =
													props.inputMap.declaration
														?.type ===
														EntityType.ValueDescriptor &&
													props.inputMap.declaration
														?.parent.type ===
														EntityType.Search;

												if (
													isInferredProperty ||
													isSearchInput
												) {
													EditorService.navigate(
														`${props.project?.editorBasePath}/entity/${props.inputMap.declaration?.type}/${props.inputMap.declaration?.id}`
													);
													return;
												}

												EditorService.navigate(
													`${props.project?.editorBasePath}/entity/${EntityType.InputMap}/${props.inputMap.id}`
												);
											}}
										/>
									</div>
								)}
							<LogicErrorIconMenu
								entity={inputMap}
								project={props.project}
								canvasElement={props.canvasElement}
								parentDisabledReasons={
									relevantParentDisabledReasons
								}
							/>
							<EntityLockedIconMenu
								entity={inputMap}
								project={props.project}
								canvasElement={props.canvasElement}
								parentDisabledReasons={
									relevantParentDisabledReasons
								}
							/>
						</span>
					</>
				)}
			</DynamicValueEntry>
		</li>
	);
}
